import type { Sortable, UniversallyUniqueId } from '../common'
import type { BLOCK_TYPE, UNITS } from '../enums'

import type { IBlock, BlockBaseSettings } from './base'

export type DeviceOptionFactorCustomMappings = {
  /**
   * The Multiplication factor adjusts the calculation’s result by multiplying it with quantity and demand:
   * Multiplication factor = Quantity x Demand x Multiplication factor
   *
   * The Unitary demand factor refines the calculation's result by multiplying quantity and unitary demand:
   * Unitary demand factor = Quantity x Unitary Demand
   */
  multiplicationFactor?: number
  quantity: number
  unitaryDemand?: number
}

export enum DEVICE_OPTION_FACTOR_PRESET {
  CUSTOM = 'custom',
  GAS = 'gas',
  POWER = 'power',
  WATER_HEATER = 'water_heater'
}

export interface IDeviceFactor {
  /**
   * The preset type of device option factor
   *  valid values are: 'gas', 'power', 'water_heater', 'custom'
   */
  preset: DEVICE_OPTION_FACTOR_PRESET
  /**
   * Custom mappings for the device option factor
   * must exist when {@link IDeviceFactor.preset} is {@link DEVICE_OPTION_FACTOR_PRESET.CUSTOM}
   */
  customMappings?: never | DeviceOptionFactorCustomMappings[]
}

export interface DeviceOptionFactor extends IDeviceFactor {
  preset:
    | DEVICE_OPTION_FACTOR_PRESET.GAS
    | DEVICE_OPTION_FACTOR_PRESET.POWER
    | DEVICE_OPTION_FACTOR_PRESET.WATER_HEATER
  customMappings?: never
}

export interface DeviceOptionCustomFactor extends IDeviceFactor {
  preset: DEVICE_OPTION_FACTOR_PRESET.CUSTOM
  customMappings: DeviceOptionFactorCustomMappings[]
}

export interface InputCalculatorItem extends Sortable {
  /**
   * The id of the device option
   */
  id: string
  /**
   * Weather this item is preSelected for users of journey
   */
  preSelect: boolean
  /**
   * The label of the device option
   * also known as value in schema v3
   */
  name: string
  /**
   * Calculation factor for the device option
   */
  factor?: DeviceOptionFactor | DeviceOptionCustomFactor
}

export type InputCalculatorItemId = UniversallyUniqueId

export interface InputCalculatorBlock extends IBlock {
  type: BLOCK_TYPE.INPUT_CALCULATOR
  required: false
  settings: BlockBaseSettings & {
    /**
     * Allow additional device option entries of the given types
     */
    allowOther: boolean
    items: Record<InputCalculatorItemId, InputCalculatorItem>
    deviceUnit: UNITS
    digitsAfterDecimalPoint: number
  }
}

export type InputCalculatorValue = {
  /** Aggregated sum of each resulting value of all {@link InputCalculatorValue.devices} */
  numberInput?: string | null
  /**
   * Which unit, if any
   * TODO: migrate strings to {@link UNITS} instead
   */
  numberUnit?: string
  /** TODO: describe */
  frequencyUnit?: string
  /** Which devices are part of this calculator */
  devices?: InputCalculatorDeviceValue[]
}

export type InputCalculatorDeviceValue = {
  /**
   * Which device (e.g. Housing units).
   *
   * This maps to {@link InputCalculatorItem}, which is part of the block's settings as defined in
   * {@link InputCalculatorBlock.settings.items}.
   */
  name: string | null
  /**
   * Only applicable when {@link InputCalculatorBlock.settings.allowOther} is true
   * and the user has selected the "Other" device, therefore being asked to provide this other device's name.
   */
  otherName?: string | null
  /** How many of these devices are being considered */
  quantity: string
  /** Indicated consumption of a single item */
  unitaryConsumption: string
  /** Pre-aggregated consumption considering all quantities (e.g. quantity * unitaryConsumption) */
  consumption?: string
}
